import { useEffect, useRef, useState } from "react";

/// A manual refresh is a nuisance check, not an emergency: one per window.
export const REFRESH_COOLDOWN_MS = 5000;
/// A refresh against a warm cache lands within a frame, and a spinner that
/// mounts and unmounts unpainted makes the button look dead.
const MIN_SPIN_MS = 450;

/// Runs a refresh at once, spins for at least `MIN_SPIN_MS`, then refuses
/// another until `REFRESH_COOLDOWN_MS` after the click, whether the refresh
/// succeeded or not.
export function useRefreshCooldown(onRefresh: () => unknown) {
  const [refreshing, setRefreshing] = useState(false);
  const [cooling, setCooling] = useState(false);
  // The pending timer is the guard: two calls before a re-render still fire once.
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const spin = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Also undone when effects re-run on kept state (Fast Refresh), or the guard
  // below would refuse every later refresh.
  useEffect(
    () => () => {
      clearTimeout(timer.current);
      clearTimeout(spin.current);
      timer.current = undefined;
      setCooling(false);
      setRefreshing(false);
    },
    [],
  );

  const refresh = async () => {
    if (timer.current || refreshing) return;
    setRefreshing(true);
    setCooling(true);
    timer.current = setTimeout(() => {
      timer.current = undefined;
      setCooling(false);
    }, REFRESH_COOLDOWN_MS);
    const floor = new Promise((resolve) => {
      spin.current = setTimeout(resolve, MIN_SPIN_MS);
    });
    try {
      await onRefresh();
    } catch {
      // Callers fire and forget; a failed refresh only has to end the spinner.
    }
    await floor;
    setRefreshing(false);
  };

  return { refresh, refreshing, cooling };
}
